import {
  hasSearchPunctuation,
  SEARCH_JOINER_PUNCTUATION_REGEX,
  SEARCH_WORD_REGEX,
} from '~/chords/DropdownSingleSelect/constants';
import { findSearchTermWordCover } from '~/chords/DropdownSingleSelect/utils/findSearchTermWordCover';
import {
  foldSearchText,
  foldSearchTextWithMap,
  originalOffsetForFoldedIndex,
} from '~/chords/DropdownSingleSelect/utils/foldSearchText';

export interface SearchHighlightRange {
  start: number;
  end: number;
}

/**
 * True when a gap is only joiner punctuation and/or whitespace (Hip-Hop, d/b/a).
 */
function isSkippableHighlightGap(gap: string): boolean {
  return !/\S/u.test(gap.replace(SEARCH_JOINER_PUNCTUATION_REGEX, ''));
}

/**
 * Combine ranges left-to-right when `shouldMerge` says the next span belongs
 * with the current one.
 */
function mergeHighlightRanges(
  ranges: SearchHighlightRange[],
  shouldMerge: (
    lastRange: SearchHighlightRange,
    range: SearchHighlightRange,
  ) => boolean,
): SearchHighlightRange[] {
  if (ranges.length === 0) {
    return [];
  }

  const sortedRanges = [...ranges].sort((rangeA, rangeB) => {
    return rangeA.start - rangeB.start;
  });
  const mergedRanges = [{ ...sortedRanges[0] }];

  for (const range of sortedRanges.slice(1)) {
    const lastRange = mergedRanges[mergedRanges.length - 1];

    if (shouldMerge(lastRange, range)) {
      lastRange.end = Math.max(lastRange.end, range.end);
    } else {
      mergedRanges.push({ ...range });
    }
  }

  return mergedRanges;
}

/**
 * Merge overlaps and joiner-separated gaps from a single compacted term
 * (hiphop → Hip-Hop, dba → d/b/a).
 */
function mergeRangesForTerm(
  label: string,
  ranges: SearchHighlightRange[],
): SearchHighlightRange[] {
  return mergeHighlightRanges(ranges, (lastRange, range) => {
    const gap = label.slice(lastRange.end, range.start);

    return range.start <= lastRange.end || isSkippableHighlightGap(gap);
  });
}

/**
 * Merge overlapping or touching spans from different terms, but leave
 * joiner-separated hits from independent tokens as separate marks.
 */
function mergeOverlappingRanges(
  ranges: SearchHighlightRange[],
): SearchHighlightRange[] {
  return mergeHighlightRanges(ranges, (lastRange, range) => {
    return range.start <= lastRange.end;
  });
}

/**
 * Map search terms onto original-label index ranges for highlighting.
 * Matching uses accent-folded text; returned offsets stay on the raw label
 * so "axe" still wraps "Axé".
 */
export function getSearchHighlightRanges(
  label: string,
  searchTerms: string[],
): SearchHighlightRange[] {
  const wordRegex = new RegExp(SEARCH_WORD_REGEX.source, 'gu');
  const wordSpans = [...label.matchAll(wordRegex)].map((match) => {
    const originalWord = match[0];
    const start = match.index ?? 0;
    const { folded, indexMap } = foldSearchTextWithMap(originalWord);

    return {
      originalWord,
      foldedWord: folded,
      indexMap,
      start,
    };
  });
  const words = wordSpans.map((span) => span.foldedWord);
  const rangesByTerm: SearchHighlightRange[][] = [];
  const { folded: foldedLabel, indexMap: labelIndexMap } =
    foldSearchTextWithMap(label);

  for (const term of searchTerms) {
    const foldedTerm = foldSearchText(term);
    const termRanges: SearchHighlightRange[] = [];

    if (hasSearchPunctuation(foldedTerm)) {
      const foldedIndex = foldedLabel.indexOf(foldedTerm);

      if (foldedIndex !== -1) {
        termRanges.push({
          start: originalOffsetForFoldedIndex(
            labelIndexMap,
            label.length,
            foldedIndex,
          ),
          end: originalOffsetForFoldedIndex(
            labelIndexMap,
            label.length,
            foldedIndex + foldedTerm.length,
          ),
        });
      }
    }

    const cover = findSearchTermWordCover(foldedTerm, words);

    if (cover) {
      for (const part of cover) {
        const span = wordSpans[part.wordIndex];

        termRanges.push({
          start: span.start,
          end:
            span.start +
            originalOffsetForFoldedIndex(
              span.indexMap,
              span.originalWord.length,
              part.length,
            ),
        });
      }
    }

    rangesByTerm.push(mergeRangesForTerm(label, termRanges));
  }

  return mergeOverlappingRanges(rangesByTerm.flat());
}
